<script>
import { defaultFilter, getObjectValueByPath } from 'vuetify/lib/util/helpers'
import BaseTableFooter from './_base-table-footer/_base-table-footer.vue'

export default {
  name: 'BaseTable',
  components: { BaseTableFooter },
  props: {
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' }, // slot `subtitle` tem precedência
    showSearch: { type: Boolean, default: false },
    disableMarginTop: { type: Boolean, default: false },
    searchValue: { type: String, default: null },
    bordered: { type: Boolean, default: false },
    // Consumidor filtra por fora (applyFilters). Busca aparece e emite
    // update:searchValue, mas v-data-table NÃO filtra nativamente (evita
    // double-filter quando match usa colunas não exibidas como email/tags).
    externalSearch: { type: Boolean, default: false },
    // Debounce (ms) da busca: o campo responde na hora, mas a filtragem
    // (interna do v-data-table) e o `update:searchValue` só disparam após parar
    // de digitar. Padrão 300ms — passe 0 para desligar (propaga a cada tecla).
    searchDebounce: { type: Number, default: 300 },
    // Placeholder do campo de busca. Default `null` mantém o 'Pesquisar' genérico
    // do `input-by-type`. Passe um texto quando a busca casar um corpus MENOR do
    // que a tabela mostra: com `externalSearch`, quem filtra é o consumidor, e o
    // campo pode casar só uma coluna — sem dizer isso, quem digita "mercado"
    // esperando casar Descrição recebe vazio sem entender por quê.
    searchPlaceholder: { type: String, default: null },
    // Mostra o "x" para limpar a busca. Default `false` = comportamento atual dos
    // consumidores existentes. Ligue quando a busca for o único controle daquele
    // termo (ex: quando ela NÃO é zerada pelo "Limpar" de um filtro externo) —
    // sem isso o usuário precisa apagar o texto à mão para sair do estado filtrado.
    searchClearable: { type: Boolean, default: false },
  },

  data() {
    return {
      searchInput: '', // valor imediato do campo (responsivo, sem debounce)
      internalSearch: '', // valor "settled" (pós-debounce) p/ filtragem interna
      searchTimer: null, // timer do debounce da busca
      tablePage: 1, // usado quando consumidor não passa :page
      tableItemsPerPage: 10, // usado quando sem :items-per-page
      paginationData: {
        // snapshot do @pagination do v-data-table
        page: 1,
        itemsPerPage: 10,
        pageStart: 0,
        pageStop: 0,
        pageCount: 1,
        itemsLength: 0,
      },
      currentSort: null, // sort real capturado (vs sort-by estático)
    }
  },
  computed: {
    // Valor "settled" da busca (pós-debounce): controlado (searchValue) quando o
    // pai gerencia; senão o interno já debounced. Alimenta o v-data-table e a
    // lógica de "sem resultados". O campo em si usa `searchInput` (imediato).
    effectiveSearch() {
      return this.searchValue === null ? this.internalSearch : this.searchValue
    },
    // Linha clicavel: quando o pai escuta @click:row, o DS aplica cursor:pointer
    // nas linhas (centraliza o que era duplicado em 15+ componentes).
    hasRowClick() {
      return !!this.$listeners['click:row']
    },
    hasSubtitle() {
      return !!(
        (this.subtitle && this.subtitle.length) ||
        this.$slots.subtitle ||
        this.$scopedSlots.subtitle
      )
    },
    // Consumidor pode fornecer um `footer` slot próprio (ex: paginação manual
    // client-side com prev/next). Nesse caso renderizamos o slot DELE no lugar do
    // BaseTableFooter e NÃO repassamos `footer` ao v-data-table interno (que já
    // tem hide-default-footer) — senão o footer renderizaria duplicado/suprimido.
    hasFooterSlot() {
      return !!(this.$scopedSlots.footer || this.$slots.footer)
    },
    // Slots repassados ao v-data-table interno — todos menos `footer` (esse é
    // tratado por fora, substituindo o BaseTableFooter). Object.keys estático por
    // uso, não muda em runtime.
    passthroughSlotNames() {
      return Object.keys(this.$scopedSlots).filter((name) => name !== 'footer')
    },
    // O consumidor controla a página? (server-side / paginação controlada).
    // Nesse caso NÃO bindamos :page interno — quem manda é o $attrs.page.
    isPageControlled() {
      return 'page' in this.$attrs
    },
    // Mesmo contrato p/ items-per-page: quando o consumidor passa
    // :items-per-page, ele é dono (server-side, ex: transcript-history). Caso
    // contrário usamos o `tableItemsPerPage` interno.
    isItemsPerPageControlled() {
      return 'items-per-page' in this.$attrs
    },
    // Server-side: consumidor passa `server-items-length` (total real no backend,
    // ex: transcript-history paginado por offset). Nesse modo o total/pageCount
    // vêm das PROPS — o `@pagination` interno do v-data-table não reflete o total
    // server-side de forma confiável (fica itemsLength=0 → footer "0 de 0" sem nav).
    // `server-items-length` é o sinal CORRETO de server-side pelo footer do
    // BaseTable: sem ela o v-data-table não sabe o total e trata os itens da página
    // como o dataset inteiro (sempre 1 página). As tabelas da plataforma que
    // paginam server-side SEM essa prop o fazem por fora (ChartPagination/
    // CrmPagination + footer próprio) e não passam pelo BaseTableFooter.
    isServerSide() {
      return 'server-items-length' in this.$attrs
    },
    // Snapshot de paginação derivado das props no modo server-side (determinístico,
    // independe do timing do @pagination). Total = server-items-length; página e
    // page-size do consumidor (controlados) ou dos internos como fallback.
    serverPagination() {
      const total = Number(this.$attrs['server-items-length']) || 0
      const perPage =
        Number(
          this.isItemsPerPageControlled
            ? this.$attrs['items-per-page']
            : this.tableItemsPerPage
        ) || 10
      const page = this.isPageControlled
        ? Number(this.$attrs.page) || 1
        : this.tablePage
      return this.buildPaginationSnapshot(total, page, perPage)
    },
    // Snapshot efetivo para o footer: server-side deriva das props; caso contrário
    // usa o capturado do @pagination (client-side, filtrado por fora, etc).
    effectivePagination() {
      return this.isServerSide ? this.serverPagination : this.paginationData
    },
    // Opções do seletor "Mostrar [N]". Usa as do consumidor (footer-props
    // itemsPerPageOptions/items-per-page-options OU :items-per-page-options) se
    // houver; senão o default. Aceita a chave camelCase E a kebab do Vuetify
    // (`items-per-page-options`) — senão server-side com page-size fixo caía no
    // default [10,25,50,100] e mostrava o seletor indevidamente.
    //
    // Nota: se o consumidor passar `:items-per-page` fixo com um valor fora
    // desta lista (ou fora do default), o v-select do "Mostrar [N]" renderiza
    // vazio (nenhum item bate com o value). Isso é intencional — a correção é
    // no consumidor (alinhar items-per-page-options com o items-per-page
    // usado), não aqui: injetar o valor corrente automaticamente quebra o
    // padrão de lista fechada usada por tabelas server-side com page-size
    // travado (ex: transcript-history, `items-per-page-options: [n]` deve
    // esconder o seletor mesmo antes do primeiro @pagination assentar).
    itemsPerPageOptions() {
      const footerProps = this.$attrs['footer-props'] || {}
      const fromFooter =
        footerProps.itemsPerPageOptions || footerProps['items-per-page-options']
      const fromAttr = this.$attrs['items-per-page-options']
      const opts = fromFooter || fromAttr
      if (Array.isArray(opts) && opts.length) return opts
      return [10, 25, 50, 100]
    },
    // Mostra o seletor de page-size só quando há mais de uma opção. Server-side
    // que fixa `items-per-page-options: [perPage]` (ex: transcript-history) cai
    // aqui com 1 opção → seletor escondido (mantém o page-size travado).
    showItemsPerPageSelector() {
      return this.itemsPerPageOptions.length > 1
    },
    // Footer do guia: "Mostrar" no lugar de "Linhas por página". Respeita
    // footer-props que o consumidor já passe (merge). Mantido para o caso de o
    // consumidor reabilitar o footer nativo via footer-props.
    footerProps() {
      return {
        itemsPerPageText: 'Mostrar',
        ...this.$attrs['footer-props'],
      }
    },
    /**
     * Quantos itens sobram depois da busca INTERNA do v-data-table.
     *
     * POR QUE PRECISAMOS CALCULAR ISTO
     * O rodapé inteiro (total, nº de páginas, "Exibindo N de M") deriva de
     * `paginationData`, que é o snapshot do `@pagination`. E o `@pagination` do
     * Vuetify 2 é emitido APENAS pelo watcher de `internalOptions`
     * (`VData.js:171-181`) — um objeto que contém page/itemsPerPage/sortBy, mas
     * NÃO `itemsLength`. Buscar muda `filteredItems` (logo `itemsLength`,
     * `pageCount` e `pageStop`) sem tocar em `internalOptions`, então o evento
     * simplesmente não dispara: as linhas filtravam certo e o rodapé seguia
     * dizendo "Exibindo 10 de 689".
     *
     * ESPELHA `searchTableItems` (VDataTable.js:20-40), que é module-local e
     * não exportado. `defaultFilter` e `getObjectValueByPath` são importados da
     * própria Vuetify para não reimplementar a comparação. As três regras que
     * o original tem e que este espelho mantém:
     *   1. `header.filter` de uma coluna vence o filtro global;
     *   2. colunas SEM `filter` casam por OU (`some`);
     *   3. colunas COM `filter` casam por E (`every`) e são aplicadas mesmo
     *      quando a busca está vazia.
     * `computedHeaders` também descarta as colunas agrupadas por `group-by` —
     * replicado aqui, porque uma coluna agrupada fora do filtro muda o
     * resultado.
     *
     * Só entra em cena na busca interna client-side; server-side e
     * `externalSearch` continuam com o total que já vinha das props/do pai.
     */
    filteredItemsLength() {
      const items = Array.isArray(this.$attrs.items) ? this.$attrs.items : []
      const busca =
        typeof this.effectiveSearch === 'string'
          ? this.effectiveSearch.trim()
          : null

      const agrupadas = this.groupByValues
      const headers = (this.$attrs.headers || []).filter(
        (h) => h && (h.value === undefined || !agrupadas.includes(h.value))
      )
      const comFiltro = headers.filter((h) => h.filter)
      const semFiltro = headers.filter((h) => !h.filter)

      const filtroGlobal = this.$attrs['custom-filter'] || defaultFilter

      let filtrados = items
      if (busca && semFiltro.length) {
        filtrados = items.filter((item) =>
          semFiltro.some(this.casaColuna(item, busca, filtroGlobal))
        )
      }
      if (comFiltro.length) {
        filtrados = filtrados.filter((item) =>
          comFiltro.every(this.casaColuna(item, busca, defaultFilter))
        )
      }
      return filtrados.length
    },
    // `group-by` chega como string ou array (contrato do v-data-table).
    groupByValues() {
      const groupBy = this.$attrs['group-by']
      if (!groupBy) return []
      return Array.isArray(groupBy) ? groupBy : [groupBy]
    },
    // A busca interna está governando a contagem? (client-side, sem filtro do
    // pai — no modo `externalSearch` quem filtra é o consumidor e o total já
    // vem correto pela prop `items`.)
    buscaInternaAtiva() {
      return !this.isServerSide && !this.externalSearch
    },
    // Quantidade de itens exibidos na página atual (pageStop - pageStart).
    displayedCount() {
      const { pageStart, pageStop } = this.effectivePagination
      return Math.max(pageStop - pageStart, 0)
    },
    // Sufixo " · ordenado por {label}" quando há sort ativo. Resolve o label a
    // partir do header cujo `value` casa com o primeiro sortBy ativo. Usa o
    // sort capturado em tempo real do v-data-table; fallback para o sort-by
    // inicial passado por prop.
    sortLabel() {
      const sortBy = this.currentSort || this.$attrs['sort-by']
      const sortValue = Array.isArray(sortBy) ? sortBy[0] : sortBy
      if (!sortValue) return ''
      const headers = this.$attrs.headers || []
      const header = headers.find((h) => h && h.value === sortValue)
      return header && header.text ? header.text : ''
    },
    // "Exibindo {N} de {total} · ordenado por {label}"
    footerSummary() {
      const base = `Exibindo ${this.displayedCount} de ${this.effectivePagination.itemsLength}`
      return this.sortLabel ? `${base} · ordenado por ${this.sortLabel}` : base
    },
    // Valor exibido no seletor "Mostrar [N]": o itemsPerPage real (server-side das
    // props, senão o do @pagination — que reflete controlado e interno).
    itemsPerPageModel() {
      return this.effectivePagination.itemsPerPage
    },
    // Mostra o rodapé quando há mais de uma página OU quando há seletor de
    // page-size utilizável (assim o usuário troca "Mostrar [N]" mesmo numa única
    // página). Continua escondido em listas pequenas sem seletor (server-side
    // com opções fixas + 1 página) — evita rodapé vazio.
    // Respeita `hide-default-footer` (prop nativa do v-data-table repassada via
    // $attrs) — quando o consumidor pede pra esconder o footer nativo, também
    // escondemos o nosso footer custom, senão o "Mostrar [N]" aparece em tabelas
    // que não deveriam ter paginação (ex: aawz-drive com 1 página só).
    showFooter() {
      if (this.hideDefaultFooter) return false
      return (
        this.effectivePagination.pageCount > 1 || this.showItemsPerPageSelector
      )
    },
    // Prop derivada do $attrs: `hide-default-footer` chega via kebab-case
    // (atributo HTML) ou `hideDefaultFooter` via camelCase (binding Vue).
    hideDefaultFooter() {
      // Respeita o VALOR, não só a presença: `:hide-default-footer="false"` deve
      // MOSTRAR o footer. Atributo "bare" (`hide-default-footer`) chega como ''
      // e esconde; binding com valor respeita o boolean. (Antes checava só
      // `!== undefined`, então `false` também escondia — o footer sumia mesmo
      // quando o consumidor pedia pra mostrar.)
      const value = this.$attrs['hide-default-footer']
      return value === '' || value === true || value === 'true'
    },
  },
  watch: {
    // Client-side filtrado por fora (ex: applyFilters no pai): o @pagination do
    // v-data-table NÃO dispara só ao trocar a prop items, então o rodapé e a
    // v-pagination ficavam stale (só atualizavam ao trocar o "Mostrar").
    // Recalculamos o paginationData na mão aqui pra manter tudo fresco.
    '$attrs.items': {
      handler(newItems, oldItems) {
        this.syncPaginationFromItems(newItems, oldItems)
      },
      deep: false,
    },
    // Se a página atual ficar maior que o total (ex: busca interna do v-data-table
    // reduz o pageCount sem trocar a prop items), clampa para uma página válida —
    // senão a v-pagination fica numa página inexistente até o usuário trocar.
    'paginationData.pageCount': {
      handler(pageCount) {
        if (!this.isPageControlled && this.tablePage > pageCount) {
          this.tablePage = Math.max(1, pageCount)
        }
      },
    },
    // Busca interna mudou → o rodapé precisa do total FILTRADO. Ver
    // `ressincronizarPaginacaoDaBusca` (o @pagination do Vuetify não dispara
    // aqui).
    effectiveSearch() {
      this.ressincronizarPaginacaoDaBusca()
    },
    // Pai muda o valor controlado por fora (ex: reset/limpar filtros) → reflete
    // no campo. Ignora null (modo interno) e o eco do que nós mesmos emitimos.
    searchValue(val) {
      if (val !== null && val !== this.searchInput) {
        this.searchInput = val
      }
    },
  },
  created() {
    // Inicializa o campo com o valor controlado (modo externo). No modo interno
    // começa vazio.
    if (this.searchValue !== null) {
      this.searchInput = this.searchValue
    }
  },
  beforeDestroy() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer)
    }
  },
  methods: {
    // Digitação: atualiza o campo na hora (responsivo) e agenda a propagação
    // (filtragem interna OU update:searchValue) após `searchDebounce` ms.
    onSearchInput(val) {
      this.searchInput = val
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
      }
      if (!this.searchDebounce) {
        this.applySearch(val)
        return
      }
      this.searchTimer = setTimeout(
        () => this.applySearch(val),
        this.searchDebounce
      )
    },
    applySearch(val) {
      if (this.searchValue === null) {
        this.internalSearch = val
      } else {
        this.$emit('update:searchValue', val)
      }
    },
    // Captura o sort ativo do v-data-table para refletir no rodapé. O evento
    // @sort emite o array de colunas ordenadas quando o usuário clica no header.
    onSort(sortBy) {
      this.currentSort = sortBy
      if (this.$listeners.sort) this.$emit('sort', sortBy)
    },
    // Captura o estado real de paginação do v-data-table (client-side filtrado
    // ou server-side). Mantém o `tablePage` interno e repassa o evento ao
    // consumidor caso ele escute @pagination.
    onPagination(pagination) {
      this.paginationData = pagination
      if (!this.isPageControlled) this.tablePage = pagination.page
      if (this.$listeners.pagination) this.$emit('pagination', pagination)
    },
    // Navegação pelo v-pagination custom. Emite update:page (preserva quem
    // escuta, ex: transcript-history server-side) e, p/ tabelas auto-geridas,
    // atualiza a página interna que está bindada no v-data-table.
    goToPage(page) {
      this.$emit('update:page', page)
      if (!this.isPageControlled) this.tablePage = page
    },
    // Troca de page-size pelo seletor "Mostrar [N]". Mesmo contrato do goToPage:
    // emite update:items-per-page (consumidor controlado é dono) e, p/
    // auto-geridas, atualiza o interno. Volta p/ a página 1 (igual footer nativo)
    // p/ não cair numa página inexistente com o novo tamanho.
    changeItemsPerPage(value) {
      this.$emit('update:items-per-page', value)
      if (!this.isItemsPerPageControlled) {
        this.tableItemsPerPage = value
        if (!this.isPageControlled) this.tablePage = 1
      }
    },

    // Recalcula o paginationData quando `items` muda por fora (client-side
    // filtrado): o @pagination do v-data-table não dispara só ao trocar a prop,
    // então o rodapé/paginação ficavam stale. Extraído do watcher p/ legibilidade.
    syncPaginationFromItems(newItems, oldItems) {
      if (this.hideDefaultFooter || this.isPageControlled) return

      const items = Array.isArray(newItems) ? newItems : []
      const oldLen = Array.isArray(oldItems) ? oldItems.length : 0
      // Com busca interna ativa, o total do rodapé é o FILTRADO — antes este
      // caso saía cedo (`return`) e o rodapé ficava com o snapshot anterior,
      // que era o defeito quando `items` trocava no meio de uma busca.
      const total =
        this.buscaInternaAtiva && this.effectiveSearch
          ? this.filteredItemsLength
          : items.length
      const perPage =
        this.paginationData.itemsPerPage || this.tableItemsPerPage || 10
      const pageCount = this.countPages(total, perPage)
      if (items.length !== oldLen) {
        this.tablePage = 1
      } else if (this.tablePage > pageCount) {
        this.tablePage = Math.max(1, pageCount)
      }
      this.paginationData = this.buildPaginationSnapshot(
        total,
        this.tablePage,
        perPage
      )
    },

    // Predicado de UMA coluna, espelhando `filterFn` (VDataTable.js:20-25):
    // `header.filter` da coluna vence o filtro global.
    casaColuna(item, busca, filtroGlobal) {
      return (header) => {
        const valor = getObjectValueByPath(item, header.value)
        return header.filter
          ? header.filter(valor, busca, item)
          : filtroGlobal(valor, busca, item)
      }
    },

    /**
     * Refaz o snapshot de paginação quando a busca INTERNA muda.
     *
     * Existe porque o `@pagination` do Vuetify não dispara nesse caso (ver
     * `filteredItemsLength`). Volta para a página 1 — mesmo comportamento do
     * footer nativo ao trocar o page-size, e a única página garantidamente
     * válida depois de filtrar (buscar na página 3 deixava o usuário numa
     * página que o resultado filtrado nem tem).
     */
    ressincronizarPaginacaoDaBusca() {
      if (this.hideDefaultFooter) return
      // externalSearch: quem filtra é o pai (a prop `items` chega filtrada e o
      // total vem dela), mas trocar o termo volta à página 1 como a busca
      // INTERNA deste componente já faz (abaixo) — antes só voltava quando o
      // nº de linhas mudava (syncPaginationFromItems). O Vuetify 2.0.11 em si
      // não zera a página na busca, só clampa ao pageCount (VData.js:359).
      // Com `:page` controlado pelo pai nada muda (server-side/paginação própria).
      if (this.externalSearch) {
        if (!this.isPageControlled) this.tablePage = 1
        return
      }
      if (!this.buscaInternaAtiva) return

      const perPage =
        this.paginationData.itemsPerPage || this.tableItemsPerPage || 10
      if (!this.isPageControlled) this.tablePage = 1
      const page = this.isPageControlled
        ? Number(this.$attrs.page) || 1
        : this.tablePage
      this.paginationData = this.buildPaginationSnapshot(
        this.filteredItemsLength,
        page,
        perPage
      )
    },

    // `-1` = "mostrar todos" (convenção do v-data-table) → 1 página; senão o
    // ceil(total / perPage). Isolar aqui evita `total / -1` quebrar a conta.
    countPages(length, perPage) {
      return perPage === -1 ? 1 : Math.max(1, Math.ceil(length / perPage))
    },

    // Snapshot de paginação para o rodapé, tratando `-1` (todos) sem quebrar.
    buildPaginationSnapshot(length, page, perPage) {
      const showAll = perPage === -1
      const pageStart = length === 0 || showAll ? 0 : (page - 1) * perPage
      const pageStop = showAll ? length : Math.min(pageStart + perPage, length)
      return {
        page,
        itemsPerPage: perPage,
        pageStart,
        pageStop,
        pageCount: this.countPages(length, perPage),
        itemsLength: length,
      }
    },
  },
}
</script>

<template>
  <div
    class="table-new-ux-v1"
    :class="{
      'table-new-ux-v1--clickable': hasRowClick,
      'az-card-bordered base-table--bordered az-table-bleed': bordered,
    }"
  >
    <div class="d-flex justify-content-between">
      <div>
        <h1 v-if="title && title.length" class="az-ds-table-title">
          {{ title }}
        </h1>
        <p v-if="hasSubtitle" class="az-ds-table-subtitle mb-0 mt-1">
          <slot name="subtitle">{{ subtitle }}</slot>
        </p>
      </div>
      <div class="d-flex align-center az-gap-small">
        <slot name="cornerAction" />
        <InputByType
          v-if="showSearch"
          :value="searchInput"
          type="search"
          max-width="250px"
          :placeholder="searchPlaceholder || undefined"
          :clearable="searchClearable"
          @input="onSearchInput"
        />
      </div>
    </div>
    <v-data-table
      v-bind="$attrs"
      :page="isPageControlled ? $attrs.page : tablePage"
      :items-per-page="
        isItemsPerPageControlled ? $attrs['items-per-page'] : tableItemsPerPage
      "
      :search="externalSearch ? '' : effectiveSearch"
      :footer-props="footerProps"
      hide-default-footer
      :class="{ 'mt-4': !disableMarginTop }"
      v-on="$listeners"
      @pagination="onPagination"
      @update:sort-by="onSort"
    >
      <template v-for="name in passthroughSlotNames" v-slot:[name]="slotProps">
        <slot :name="name" v-bind="slotProps" />
      </template>
    </v-data-table>

    <!-- Footer próprio do consumidor tem precedência sobre o BaseTableFooter -->
    <slot v-if="hasFooterSlot" name="footer" v-bind="effectivePagination" />
    <BaseTableFooter
      v-else-if="showFooter"
      :show-items-per-page-selector="showItemsPerPageSelector"
      :items-per-page-model="itemsPerPageModel"
      :items-per-page-options="itemsPerPageOptions"
      :footer-summary="footerSummary"
      :pagination-data="effectivePagination"
      @change-items-per-page="changeItemsPerPage"
      @go-to-page="goToPage"
    />
  </div>
</template>
